/* DuplicatesPanel — admin review of repeated accounts, recipes, cookbooks, Kitchen Help terms and FAQs. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { recipePath, cookbookPath } from "@/lib/slug";
import { ArrowsClockwise, LockSimple, Trash } from "@phosphor-icons/react";

const SECTIONS = [
  { key: "recipes_same_owner_title", label: "Same owner, same recipe title" },
  { key: "public_recipes_same_title", label: "Public recipes sharing a title" },
  { key: "users_same_email", label: "Accounts with the same email" },
  { key: "users_same_username", label: "Accounts with the same username" },
  { key: "cookbooks_same_owner_title", label: "Same owner, same cookbook title" },
  { key: "kitchen_terms", label: "Kitchen Help terms" },
  { key: "faqs", label: "FAQs" },
];
const day = (d) => (d ? String(d).slice(0, 10) : "");

function RecipeItem({ it, onFix, busy }) {
  return (
    <li className="flex flex-wrap items-center gap-2 py-2" data-testid={`dup-recipe-${it.recipe_id}`}>
      <a href={recipePath(it)} target="_blank" rel="noreferrer" className="font-bold text-rf-ink hover:underline">{it.title || "Untitled"}</a>
      <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${it.is_public ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{it.is_public ? "Public" : "Private"}</span>
      <span className="text-xs text-rf-ink-soft">{it.owner ? `${it.owner} · ` : ""}{it.cookbook_title ? `“${it.cookbook_title}” · ` : ""}added {day(it.created_at)}</span>
      <span className="ml-auto flex gap-1.5">
        {it.is_public && <button type="button" disabled={busy} onClick={() => onFix(it, "private")} data-testid={`dup-private-${it.recipe_id}`} className="btn-outline-ink !py-1 !px-2.5 text-xs inline-flex items-center gap-1"><LockSimple size={12} /> Make private</button>}
        <button type="button" disabled={busy} onClick={() => onFix(it, "bin")} data-testid={`dup-bin-${it.recipe_id}`} className="btn-outline-ink !py-1 !px-2.5 text-xs !border-red-300 !text-red-700 inline-flex items-center gap-1"><Trash size={12} /> Move to bin</button>
      </span>
    </li>
  );
}

function PlainItem({ it }) {
  if (it.user_id) {
    return <li className="py-1.5 text-sm" data-testid={`dup-user-${it.user_id}`}><b>{it.email}</b> · @{it.username || "—"} · {it.tier || "free"} · {it.recipes} recipes · {it.has_password ? "password" : "Google"} sign-in · joined {day(it.created_at)} <span className="text-rf-ink-soft">({it.user_id})</span></li>;
  }
  if (it.cookbook_id) {
    return <li className="py-1.5 text-sm"><a href={cookbookPath(it)} target="_blank" rel="noreferrer" className="font-bold hover:underline">{it.title}</a> · {it.privacy} · added {day(it.created_at)}</li>;
  }
  return <li className="py-1.5 text-sm">{it.term || it.question} <span className="text-rf-ink-soft">({it.term_id || it.faq_id})</span></li>;
}

function Group({ g, isRecipe, onFix, busy }) {
  const heading = typeof g.key === "object" ? g.key.title : g.key;
  return (
    <li className="card-recipe !p-4" data-testid="dup-group">
      <p className="text-sm font-black text-rf-ink">“{heading}” <span className="text-rf-ink-soft font-semibold">× {g.count}{g.owner ? ` · ${g.owner}` : ""}</span></p>
      <ul className="divide-y divide-rf-ink/10 mt-1">
        {g.items.map((it, i) => (isRecipe ? <RecipeItem key={it.recipe_id || i} it={it} onFix={onFix} busy={busy} /> : <PlainItem key={i} it={it} />))}
      </ul>
    </li>
  );
}

export default function DuplicatesPanel() {
  const [data, setData] = useState(null);
  const [section, setSection] = useState(SECTIONS[0].key);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/duplicates-report").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the report")), []);
  useEffect(() => { load(); }, [load]);

  const onFix = async (it, action) => {
    if (action === "bin" && !window.confirm(`Move “${it.title}” to its owner's bin? They can restore it for 30 days.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/duplicates/recipe/${it.recipe_id}`, { action });
      toast.success(action === "bin" ? "Moved to the owner's bin" : "Made private");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(false); }
  };

  if (!data) return <p className="text-rf-ink-soft" data-testid="dup-loading">Checking for duplicates…</p>;
  const groups = data[section] || [];
  const isRecipe = section.startsWith("recipes_") || section.startsWith("public_recipes");
  return (
    <div data-testid="duplicates-panel">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display font-black text-2xl text-rf-ink">Duplicates</h2>
          <p className="text-sm text-rf-ink-soft max-w-2xl">Repeated content across the site. Recipe fixes are reversible: “Make private” hides it from Google and the directory; “Move to bin” sends it to the owner&apos;s bin for 30 days.</p>
        </div>
        <button type="button" onClick={load} data-testid="dup-refresh" className="btn-outline-ink !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5"><ArrowsClockwise size={14} /> Refresh</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        {SECTIONS.map((s) => (
          <button key={s.key} type="button" onClick={() => setSection(s.key)} data-testid={`dup-section-${s.key}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold border ${section === s.key ? "bg-rf-ink text-white border-rf-ink" : "bg-white border-rf-ink/20 text-rf-ink"}`}>
            {s.label} <span className={data.summary[s.key] ? "text-rf-orange" : "opacity-60"}>{data.summary[s.key]}</span>
          </button>
        ))}
      </div>
      {groups.length === 0 ? <p className="text-rf-ink-soft" data-testid="dup-none">No duplicates here.</p> : (
        <ul className="space-y-3">{groups.map((g, i) => <Group key={i} g={g} isRecipe={isRecipe} onFix={onFix} busy={busy} />)}</ul>
      )}
    </div>
  );
}
